import { useCallback, useEffect, useRef, useState } from 'react'

// Speech recognition is still vendor-prefixed in Safari, and Firefox does not
// ship it at all, so the caller hides its microphone when `supported` is false.
const speechRecognition = () => {
  if (typeof window === 'undefined') return null
  return window.SpeechRecognition || window.webkitSpeechRecognition || null
}

const ERROR_COPY = {
  'not-allowed':
    'Microphone access is blocked. Allow it in your browser settings, then try again.',
  'service-not-allowed':
    'Microphone access is blocked. Allow it in your browser settings, then try again.',
  'audio-capture': 'No microphone was found.',
  network: 'Dictation needs a network connection.',
}

// A session that ends this quickly never really started, so restarting it would
// only spin.
const MIN_SESSION_MS = 300

const joinWords = (existing, addition) =>
  existing ? `${existing.replace(/\s+$/, '')} ${addition}` : addition

// Dictation for the assistant composer. Recognized speech is handed to
// `onTranscript` so the admin can edit it before sending, and the phrase still
// being recognized is exposed as `interimText` for a live caption.
export default function useSpeechDictation({ onTranscript } = {}) {
  const Recognition = speechRecognition()
  const supported = Boolean(Recognition)

  const [listening, setListening] = useState(false)
  const [interimText, setInterimText] = useState('')
  const [error, setError] = useState('')

  const recognitionRef = useRef(null)
  // Chrome ends a continuous session on its own after a pause, so track whether
  // the admin still wants to dictate and restart until they say otherwise.
  const wantedRef = useRef(false)
  const startedAtRef = useRef(0)
  const transcriptRef = useRef(onTranscript)
  const pendingRef = useRef('')
  const heardRef = useRef(false)
  // Once the admin stops, the pending phrase has already been handed over, so
  // late results from the closing session would duplicate it.
  const ignoreResultsRef = useRef(false)

  useEffect(() => {
    transcriptRef.current = onTranscript
  }, [onTranscript])

  // The browser only marks a phrase final once it decides the speaker paused,
  // so a session that ends mid-phrase still holds words the admin said. Hand
  // those over instead of dropping them.
  const commitPending = useCallback(() => {
    const pending = pendingRef.current.trim()
    pendingRef.current = ''
    setInterimText('')
    if (pending) transcriptRef.current?.(pending)
  }, [])

  const stop = useCallback(() => {
    // Closing the panel stops dictation too, and that is not a session the
    // admin needs feedback about.
    const wasListening = wantedRef.current
    wantedRef.current = false
    ignoreResultsRef.current = true
    setListening(false)
    commitPending()
    // Otherwise a muted or wrong input device just looks like a button that
    // does nothing.
    if (wasListening && !heardRef.current) {
      setError(
        'No speech was picked up. Check which microphone your browser is using, then try again.'
      )
    }
    try {
      recognitionRef.current?.stop()
    } catch (_error) {
      // Stopping a session that already ended is not a failure.
    }
  }, [commitPending])

  const start = useCallback(() => {
    if (!supported || wantedRef.current) return

    setError('')
    let recognition = recognitionRef.current

    if (!recognition) {
      recognition = new Recognition()
      recognition.continuous = true
      recognition.interimResults = true
      recognition.lang =
        (typeof navigator !== 'undefined' && navigator.language) || 'en-US'

      recognition.onresult = (event) => {
        if (ignoreResultsRef.current) return
        let finalText = ''
        let pendingText = ''
        for (
          let index = event.resultIndex;
          index < event.results.length;
          index++
        ) {
          const result = event.results[index]
          const text = result?.[0]?.transcript || ''
          if (result?.isFinal) finalText += text
          else pendingText += text
        }
        // A finalized phrase is no longer pending, so this never double counts.
        pendingRef.current = pendingText
        setInterimText(pendingText.trim())
        const finished = finalText.trim()
        if (finished || pendingText.trim()) heardRef.current = true
        if (finished) transcriptRef.current?.(finished)
      }

      recognition.onerror = (event) => {
        // Silence and a deliberate stop both end the session normally.
        if (event?.error === 'no-speech' || event?.error === 'aborted') return
        wantedRef.current = false
        setListening(false)
        commitPending()
        setError(
          ERROR_COPY[event?.error] ||
            'Dictation stopped unexpectedly. Try again or type your message.'
        )
      }

      recognition.onend = () => {
        commitPending()
        if (
          !wantedRef.current ||
          Date.now() - startedAtRef.current < MIN_SESSION_MS
        ) {
          wantedRef.current = false
          setListening(false)
          return
        }
        try {
          startedAtRef.current = Date.now()
          recognitionRef.current?.start()
        } catch (_error) {
          wantedRef.current = false
          setListening(false)
        }
      }

      recognitionRef.current = recognition
    }

    wantedRef.current = true
    ignoreResultsRef.current = false
    heardRef.current = false
    startedAtRef.current = Date.now()
    try {
      recognition.start()
    } catch (_error) {
      // start() throws when a session is already running, which is harmless.
    }
    setListening(true)
  }, [Recognition, commitPending, supported])

  useEffect(
    () => () => {
      wantedRef.current = false
      try {
        recognitionRef.current?.abort()
      } catch (_error) {
        // Nothing to clean up when no session was ever started.
      }
    },
    []
  )

  return { supported, listening, interimText, error, start, stop }
}

export { joinWords }
